import { useEffect, useState } from 'react'
import { Modal } from '../../../../customComponents/Modal'
import { type RCNPD } from '../../../../../models/RCNPD'
import { Accordion, AccordionDetails, AccordionSummary, FormControl, FormControlLabel, FormLabel, InputAdornment, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material'
import { toast } from 'react-toastify'
import { AddRCNPD, UpdateRCNPD } from '../../../../../services/AnnualPlanService'
import { type UpdateTrainingFormValues } from './AddEditTrainingModal'
import { type RCNPDSeriesSession, type UpdateRCNPDSeriesSession } from '../../../../../models/RCNPDSeriesSession'
import { TrainingLength } from '../../../../../models/TrainingLength'
import { ExpandMore } from '@mui/icons-material'
import { isValidPositiveNumber } from '../../../../../utils/isValidNumber'

export interface TrainingTrackingFormValues {
  trainingHeld: boolean | null
  numberOfAttendees: string
  actualBudgetAmount: string
  seriesSessions?: UpdateRCNPDSeriesSession[]
}

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
  rcnPdTraining: RCNPD
}
export const TrainingTrackingModal = (props: Props): JSX.Element => {
  const addSeriesSession = (session?: RCNPDSeriesSession): UpdateRCNPDSeriesSession => ({
    id: session?.id ?? 0,
    rcnpdUpdateId: session?.rcnpdUpdateId ?? props.rcnPdTraining.id,
    name: session?.name ?? '',
    contentFocus: session?.contentFocus ?? [],
    date: session?.date ?? null,
    length: session?.length ?? '',
    trainingHeld: session?.trainingHeld,
    numberOfAttendees: session?.numberOfAttendees ?? null,
    numberOfTeamsTrained: session?.numberOfTeamsTrained ?? null,
    budget: session?.budget ?? null,
    budgetSpent: session?.budgetSpent ?? null
  })
  const isSeries = props?.rcnPdTraining?.isSeries ?? false
  const [formValues, setFormValues] = useState<TrainingTrackingFormValues>({
    trainingHeld: props.rcnPdTraining.trainingHeld != null ? props.rcnPdTraining.trainingHeld : null,
    numberOfAttendees: props.rcnPdTraining.numberOfAttendees?.toString() ?? '',
    actualBudgetAmount: props.rcnPdTraining.actualBudgetAmount?.toString() ?? '',
    seriesSessions: props.rcnPdTraining.seriesSessions?.map(addSeriesSession) ?? []
  })
  // form validators for tracking single event
  const formValidators: Record<keyof TrainingTrackingFormValues, { required: boolean, isValid: boolean }> = {
    trainingHeld: { required: true, isValid: formValues.trainingHeld != null && (typeof formValues.trainingHeld === 'boolean' || formValues.trainingHeld === 'true' || formValues.trainingHeld === 'false') },
    numberOfAttendees: { required: true, isValid: formValues.numberOfAttendees !== '' && !isNaN(Number(formValues.numberOfAttendees)) && Number(formValues.numberOfAttendees) >= 0 },
    actualBudgetAmount: { required: false, isValid: !isValidPositiveNumber(formValues.actualBudgetAmount) },
    seriesSessions: { required: false, isValid: true }
  }
  // form validators for tracking series session
  const sessionUpdateFormValidators = (session: UpdateRCNPDSeriesSession): Record<keyof UpdateRCNPDSeriesSession, { required: boolean, isValid: boolean }> => {
    const validators = {
      id: { required: true, isValid: session.id > 0 },
      rcnpdUpdateId: { required: true, isValid: session.rcnpdUpdateId > 0 },
      name: { required: true, isValid: session.name.trim() !== '' },
      contentFocus: { required: true, isValid: session.contentFocus.length > 0 },
      date: { required: true, isValid: session.date != null && new Date(session.date) instanceof Date && !isNaN(new Date(session.date).getTime()) },
      length: { required: true, isValid: Object.values(TrainingLength).includes(session.length as TrainingLength) },
      trainingHeld: { required: true, isValid: session.trainingHeld != null && (typeof session.trainingHeld === 'boolean' || (session.trainingHeld === 'true' || session.trainingHeld === 'false')) },
      numberOfAttendees: { required: true, isValid: session.numberOfAttendees != null && (!isNaN(Number(session.numberOfAttendees)) && Number(session.numberOfAttendees) >= 0) },
      numberOfTeamsTrained: { required: true, isValid: session.numberOfTeamsTrained != null && (!isNaN(Number(session.numberOfTeamsTrained)) && Number(session.numberOfTeamsTrained) >= 0) },
      budget: { required: false, isValid: session.budget != null && (session.budget === '' || (!isNaN(Number(session.budget)) && Number(session.budget) >= 0)) },
      budgetSpent: { required: false, isValid: !isValidPositiveNumber(formValues.actualBudgetAmount) },
      otherContentFocus: { required: false, isValid: true }
    }
    return validators
  }
  const hasInvalidSessionFormValues = (session: UpdateRCNPDSeriesSession): boolean => {
    return Object.entries(session).some(([key, value]) => {
      const validator = sessionUpdateFormValidators(session)[key as keyof UpdateRCNPDSeriesSession]
      return validator.required && !validator.isValid
    })
  }
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    setFormValues({
      trainingHeld: props.rcnPdTraining.trainingHeld != null ? props.rcnPdTraining.trainingHeld : null,
      numberOfAttendees: props.rcnPdTraining.numberOfAttendees?.toString() ?? '',
      actualBudgetAmount: props.rcnPdTraining.actualBudgetAmount?.toString() ?? '',
      seriesSessions: props.rcnPdTraining.seriesSessions?.map(addSeriesSession) ?? []
    })
  }, [props.rcnPdTraining])

  const handleFieldUpdate = (e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleSubmit = async (): Promise<void> => {
    setSubmitted(true)
    if (isSeries) {
      if (props.rcnPdTraining.seriesSessions == null || props.rcnPdTraining.seriesSessions.length === 0) {
        toast.error('No sessions found for this training series')
        return
      }

      if (formValues.seriesSessions?.some(session => !isValidPositiveNumber(session.budgetSpent)) === true) {
        toast.error('Budget must be greater than or equal to 0 ')
        return
      } else if (formValues.seriesSessions?.some(hasInvalidSessionFormValues) === true) {
        toast.error('Please fill out required fields for all sessions')
        return
      }
    } else {
      const invalidFormValues = Object.entries(formValidators)
        .filter(([_, validator]) => validator.required && !validator.isValid)
        .map(([fieldName, _]) => fieldName)

      if (invalidFormValues.includes('budgetRequest')) {
        if (!isValidPositiveNumber(formValues.actualBudgetAmount)) {
          toast.error('Budget must be greater than or equal to 0')
          return
        }
      } else if (invalidFormValues.length > 0) {
        toast.error('Please fill out required fields')
        return
      }
    }

    const updatedRcnPd: UpdateTrainingFormValues = {
      id: props.rcnPdTraining.id,
      rcnpdId: props.rcnPdTraining.rcnpdId,
      priorityId: props.rcnPdTraining.priorityId,
      title: props.rcnPdTraining.title,
      organizationIds: props.rcnPdTraining.organizations.map(org => org.id),
      trainingFormat: props.rcnPdTraining.trainingFormat,
      scope: props.rcnPdTraining.scope,
      open: props.rcnPdTraining.open,
      resourcesNeeded: props.rcnPdTraining.resourcesNeeded,
      budgetRequest: props.rcnPdTraining.budgetRequest.toString(),
      isSeries,
      // additional details
      contentFocus: isSeries ? undefined : props.rcnPdTraining.contentFocus,
      trainingDate: isSeries ? undefined : props.rcnPdTraining.trainingDate,
      trainingLength: isSeries ? undefined : props.rcnPdTraining.trainingLength,
      seriesSessions: isSeries ? formValues.seriesSessions : [],
      // implementation properties
      isImplementationPhase: true,
      isUpdate: props.rcnPdTraining.isUpdate === true,
      // tracking details
      trainingHeld: isSeries ? null : formValues.trainingHeld,
      numberOfAttendees: isSeries ? null : formValues.numberOfAttendees,
      numberOfTeamsTrained: isSeries ? null : null,
      actualBudgetAmount: formValues.actualBudgetAmount
    }

    const { success, errors } = props.rcnPdTraining.isUpdate === true
      ? await UpdateRCNPD(updatedRcnPd)
      : await AddRCNPD(updatedRcnPd)
    if (!success) {
      errors.forEach(error => toast.error(error))
      return
    }

    toast.success(isSeries ? 'Training Series Data Tracked' : 'Single Training Event Data Tracked')
    props.onSaved()
    props.onClose()
  }

  const renderTrackingFields = (): JSX.Element | null => {
    if (isSeries) {
      if (formValues.seriesSessions == null || formValues.seriesSessions.length === 0) {
        return <Typography variant='body1'>No sessions found for this training series</Typography>
      }

      interface sessionFieldEvent { target: { name: string, value: string | boolean } }
      const handleSessionFieldUpdate = (index: number, e: sessionFieldEvent): void => {
        const { name, value } = e.target
        if (formValues.seriesSessions == null || formValues.seriesSessions[index] == null) return
        const updatedSessions = [...formValues.seriesSessions]
        const sessionToUpdate = { ...updatedSessions[index], [name]: value }
        updatedSessions[index] = sessionToUpdate
        setFormValues({ ...formValues, seriesSessions: updatedSessions })
      }
      if (formValues.seriesSessions.length < 2) {
        const session = formValues.seriesSessions[0]
        const index = 0
        return (
          <>
            <FormControl
              required
              data-testid={`trainingHeld-${index}`}
              error={submitted && session.trainingHeld == null}
            >
              <FormLabel>Was the training series session held?</FormLabel>
              <RadioGroup
                row
                aria-labelledby="trainingHeldRadioButtonsGroup"
                name="trainingHeld"
                value={session.trainingHeld ?? ''}
                onChange={(e) => { handleSessionFieldUpdate(index, e) }}
              >
                <FormControlLabel data-testid='trainingHeldYes' value={true} control={<Radio />} label="Yes" />
                <FormControlLabel data-testid='trainingHeldNo' value={false} control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>

            <Stack>
              <Typography variant='body1'>How many people attended?</Typography>
              <TextField
                required
                id={`numberOfAttendees-${index}`}
                sx={{ mt: '.75em', width: '250px' }}
                variant='filled'
                onChange={(e) => { handleSessionFieldUpdate(index, e) }}
                label={'Enter Number'}
                data-testid={`numberOfAttendees-${index}`}
                name='numberOfAttendees'
                error={submitted && (session.numberOfAttendees == null || session.numberOfAttendees === '' || isNaN(Number(session.numberOfAttendees)) || Number(session.numberOfAttendees) < 0)}
                value={session.numberOfAttendees ?? ''}
              />
            </Stack>

            <Stack>
              <Typography variant='body1'>How many teams were trained?</Typography>
              <TextField
                required
                id={`numberOfTeamsTrained-${index}`}
                sx={{ mt: '.75em', width: '250px' }}
                variant='filled'
                onChange={(e) => { handleSessionFieldUpdate(index, e) }}
                label={'Enter Number'}
                data-testid={`numberOfTeamsTrained-${index}`}
                name='numberOfTeamsTrained'
                error={submitted && (session.numberOfTeamsTrained == null || session.numberOfTeamsTrained === '' || isNaN(Number(session.numberOfTeamsTrained)) || Number(session.numberOfTeamsTrained) < 0)}
                value={session.numberOfTeamsTrained ?? ''}
              />
            </Stack>

            <Stack>
              <Typography variant='h5'>Budget</Typography>
              <Typography variant='body1'>
                How much funding was actually used?
                <Typography component='span' variant='caption' color='var(--grey-500)' ml='.25em'>Optional</Typography>
              </Typography>
              <TextField
                id={`budgetSpent-${index}`}
                sx={{ mt: '.75em', width: '250px' }}
                variant='filled'
                onChange={(e) => { handleSessionFieldUpdate(index, e) }}
                label={'Enter Amount'}
                data-testid={`budgetSpent-${index}`}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                name='budgetSpent'
                required={false}
                error={submitted && (session.budgetSpent != null && (isNaN(Number(session.budgetSpent)) || Number(session.budgetSpent) < 0))}
                value={session.budgetSpent ?? ''}
              />
            </Stack>
          </>
        )
      } else {
        return <>
          {formValues.seriesSessions.map((session, index) => (
            <Stack
              key={index}
              gap='1rem'
              sx={{
                border: '1px solid rgba(225, 225, 226, 1)',
                borderRadius: '8px'
              }}
            >
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls={`sessionAccordionContent-${index}`}
                  id={`sessionAccordionHeader-${index}`}
                >
                  <Typography component="span" variant='h5' fontSize='18px'>{session.name}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <FormControl
                    required
                    key={`series-session-${index}`}
                    data-testid={`trainingHeld-${index}`}
                    error={submitted && session.trainingHeld == null}
                  >
                    <FormLabel>Was the training held?</FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="trainingHeldRadioButtonsGroup"
                      name="trainingHeld"
                      value={session.trainingHeld ?? ''}
                      onChange={(e) => { handleSessionFieldUpdate(index, e) }}
                    >
                      <FormControlLabel data-testid='trainingHeldYes' value={true} control={<Radio />} label="Yes" />
                      <FormControlLabel data-testid='trainingHeldNo' value={false} control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>

                  <Stack>
                    <Typography variant='body1'>How many people attended?</Typography>
                    <TextField
                      required
                      id={`numberOfAttendees-${index}`}
                      sx={{ mt: '.75em', width: '250px' }}
                      variant='filled'
                      onChange={(e) => { handleSessionFieldUpdate(index, e) }}
                      label={'Enter Number'}
                      data-testid={`numberOfAttendees-${index}`}
                      name='numberOfAttendees'
                      error={submitted && (session.numberOfAttendees == null || session.numberOfAttendees === '' || isNaN(Number(session.numberOfAttendees)) || Number(session.numberOfAttendees) < 0)}
                      value={session.numberOfAttendees ?? ''}
                    />
                  </Stack>

                  <Stack>
                    <Typography variant='body1'>How many teams were trained?</Typography>
                    <TextField
                      required
                      id={`numberOfTeamsTrained-${index}`}
                      sx={{ mt: '.75em', width: '250px' }}
                      variant='filled'
                      onChange={(e) => { handleSessionFieldUpdate(index, e) }}
                      label={'Enter Number'}
                      data-testid={`numberOfTeamsTrained-${index}`}
                      name='numberOfTeamsTrained'
                      error={submitted && (session.numberOfTeamsTrained == null || session.numberOfTeamsTrained === '' || isNaN(Number(session.numberOfTeamsTrained)) || Number(session.numberOfTeamsTrained) < 0)}
                      value={session.numberOfTeamsTrained ?? ''}
                    />
                  </Stack>

                  <Stack>
                    <Typography variant='h5'>Budget</Typography>
                    <Typography variant='body1'>
                      How much funding was actually used?
                      <Typography component='span' variant='caption' color='var(--grey-500)' ml='.25em'>Optional</Typography>
                    </Typography>
                    <TextField
                      id={`budgetSpent-${index}`}
                      sx={{ mt: '.75em', width: '250px' }}
                      variant='filled'
                      onChange={(e) => { handleSessionFieldUpdate(index, e) }}
                      label={'Enter Amount'}
                      data-testid={`budgetSpent-${index}`}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      name='budgetSpent'
                      required={false}
                      error={submitted && (session.budgetSpent != null && (isNaN(Number(session.budgetSpent)) || Number(session.budgetSpent) < 0))}
                      value={session.budgetSpent ?? ''}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          ))}
        </>
      }
    }

    return (
      <>
        <FormControl
          data-testid='trainingHeld'
          required={formValidators.trainingHeld.required}
          error={submitted && !formValidators.trainingHeld.isValid}
        >
          <FormLabel>Was the training held?</FormLabel>
          <RadioGroup
            row
            aria-labelledby="trainingHeldRadioButtonsGroup"
            name="trainingHeld"
            value={formValues.trainingHeld ?? ''}
            onChange={handleFieldUpdate}
          >
            <FormControlLabel data-testid='trainingHeldYes' value={true} control={<Radio />} label="Yes" />
            <FormControlLabel data-testid='trainingHeldNo' value={false} control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>

        <Stack>
          <Typography variant='body1'>How many people attended?</Typography>
          <TextField
            id='numberOfAttendees'
            sx={{ mt: '.75em', width: '250px' }}
            variant='filled'
            onChange={handleFieldUpdate}
            label={'Enter Number'}
            data-testid='numberOfAttendees'
            name='numberOfAttendees'
            required={formValidators.numberOfAttendees.required}
            error={submitted && !formValidators.numberOfAttendees.isValid}
            value={formValues.numberOfAttendees ?? ''}
          />
        </Stack>

        <Stack>
          <Typography variant='h5'>Budget</Typography>
          <Typography variant='body1'>
            How much funding was actually used?
            <Typography component='span' variant='caption' color='var(--grey-500)' ml='.25em'>Optional</Typography>
          </Typography>
          <TextField
            id='actualBudgetAmount'
            sx={{ mt: '.75em', width: '250px' }}
            variant='filled'
            onChange={handleFieldUpdate}
            label={'Enter Amount'}
            data-testid='actualBudgetAmount'
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            name='actualBudgetAmount'
            required={formValidators.actualBudgetAmount.required}
            error={submitted && !formValidators.actualBudgetAmount.isValid}
            value={formValues.actualBudgetAmount ?? ''}
          />
        </Stack>
      </>
    )
  }

  return (
    <Modal
      title={isSeries ? 'Training Series Tracking' : 'Single Event Tracking'}
      open={props.open}
      data-testid='trainingTrackingModal'
      width='480px'
      confirmButtonText='Save'
      cancelButtonText='Cancel'
      onClose={props.onClose}
      onConfirm={handleSubmit}
      bodyContent={<Stack gap='1rem' m='24px 0'>
        {renderTrackingFields()}
      </Stack>}
    />
  )
}
